import { CheckIcon, TriangleAlertIcon } from "lucide-react";

import { Etiqueta } from "@/components/ui/etiqueta";
import type { Hecho } from "@/lib/generacion";
import { filasDeLaVerificacion, type FilaDeVerificacion, type Verificacion } from "@/lib/redaccion";
import { cn } from "@/lib/utils";

interface VerificacionDeLaSeccionProps {
  texto: string;
  verificacion: Verificacion | null;
  hechos: Hecho[];
  fuentes: string[];
  faltantes: string[];
}

const plural = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`;

/**
 * Lo que el servidor comprobó del texto y de dónde lo sacó la IA: dos
 * recuadros lado a lado. Los errores NO impiden guardar ni validar (los
 * decide la persona), pero se muestran de frente.
 */
export function VerificacionDeLaSeccion({ texto, verificacion, hechos, fuentes, faltantes }: VerificacionDeLaSeccionProps) {
  const filas = filasDeLaVerificacion(texto, verificacion, hechos);

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(16.25rem,1fr))] gap-3">
      <section aria-label="Verificación automática" className="border-border flex flex-col gap-2.5 rounded-[0.875rem] border-[1.5px] p-3">
        <div className="flex items-center gap-2">
          <h3 className="text-ink text-xs font-bold">Verificación automática</h3>
          <ResumenDeLaVerificacion verificacion={verificacion} />
        </div>

        {filas.length === 0 ? (
          <p className="text-ink-medio text-xs font-semibold">
            {verificacion ? "El texto no cita ninguna cifra de la hoja de hechos." : "Esta versión del texto todavía no se verificó."}
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {filas.map((f, i) => (
              <Fila key={i} fila={f} />
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Se basó en" className="border-border flex flex-col gap-2.5 rounded-[0.875rem] border-[1.5px] p-3">
        <h3 className="text-ink text-xs font-bold">Se basó en</h3>

        {fuentes.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {fuentes.map((f) => (
              <span key={f} className="bg-divisor text-ink-secundario rounded-[0.4375rem] px-2 py-[0.1875rem] text-[0.71875rem] font-semibold">
                {f}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-ink-medio text-xs font-semibold">La IA no declaró fuentes para este texto.</p>
        )}

        {faltantes.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-ink-muted text-xs font-semibold">No encontró información sobre</p>
            <ul className="flex flex-col gap-1">
              {faltantes.map((f) => (
                <li key={f} className="text-ink-secundario text-xs font-semibold">
                  {f}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ResumenDeLaVerificacion({ verificacion }: { verificacion: Verificacion | null }) {
  if (verificacion === null) return null;

  if (verificacion.estado === "errores") {
    return (
      <Etiqueta tono="danger" size="sm" className="ml-auto">
        {plural(verificacion.errores.length, "error", "errores")}
      </Etiqueta>
    );
  }
  if (verificacion.estado === "advertencias") {
    return (
      <Etiqueta tono="warning" size="sm" className="ml-auto">
        {plural(verificacion.advertencias.length, "advertencia", "advertencias")}
      </Etiqueta>
    );
  }

  return (
    <Etiqueta tono="success" size="sm" className="ml-auto">
      Sin hallazgos
    </Etiqueta>
  );
}

function Fila({ fila }: { fila: FilaDeVerificacion }) {
  return (
    <li className="text-ink-secundario flex items-baseline gap-2 text-xs font-semibold">
      <span
        aria-hidden
        className={cn(
          "flex w-3.5 shrink-0 translate-y-0.5 justify-center",
          fila.nivel === "ok" && "text-success-soft-foreground",
          fila.nivel === "aviso" && "text-warning-texto",
          fila.nivel === "error" && "text-danger-texto",
        )}
      >
        {fila.nivel === "ok" ? <CheckIcon className="size-3.5" strokeWidth={3} /> : <TriangleAlertIcon className="size-3.5" />}
      </span>
      <span className="sr-only">{fila.nivel === "ok" ? "Correcto:" : fila.nivel === "aviso" ? "Advertencia:" : "Error:"}</span>
      {fila.valor ? <span className="text-ink shrink-0 font-mono font-bold">{fila.valor}</span> : null}
      <span className="min-w-0 text-pretty">{fila.mensaje}</span>
    </li>
  );
}
